// The Terminal console's per-pane filter (#4397, epic #4391): a `key=value`
// grammar over `TerminalEvent`, applied client-side over the ONE shared
// buffer every pane reads (`lib/terminal/buffer.ts`) — never a second query,
// never a second connection. Pure, no DOM: `filter.test.ts` proves the
// grammar and the predicate without a render.
//
// GRAMMAR. Whitespace-separated tokens, `"quoted value with spaces"` allowed.
// `actor=`, `repo=`, `box=`, `level=` (case-insensitive key) narrow to one
// field each; anything else — including a token that merely LOOKS like
// `key=value` but names a key this grammar does not recognise — is bare text,
// matched as a literal case-insensitive substring against the row's rendered
// sentence, its subject label and its actor label. An unrecognised key is
// deliberately NOT a silent no-op: `format=oops` searches for the literal
// string "format=oops", which is honest (it will usually match nothing) where
// dropping the token silently would look like the filter ignored what was
// typed.
//
// WHY THIS NEVER OPENS A SECOND QUERY. `matchesTerminalFilter` takes a row
// already in hand; the caller (a pane) filters `rows()` — the shared buffer
// `Terminal.tsx` merges the backfill, the live tail and the system lines
// into. Reading further BACK than what is loaded is the SAME shared backfill
// `loadMore()` every pane already shares, up to `buffer.ts`'s
// `atScrollbackCeiling` — see `TerminalPane.tsx` for the wiring.

import type { TerminalActor, TerminalEvent, TerminalLevel } from '@developerz/domain';
import { actorLabel, levelLabel, terminalLineText } from '../terminal-events';

export type TerminalFilterQuery = {
  readonly actor: string | null;
  readonly repo: string | null;
  readonly box: string | null;
  readonly level: string | null;
  /** Bare terms, already lowercased. Every term must match (AND). */
  readonly text: readonly string[];
};

export const EMPTY_TERMINAL_FILTER: TerminalFilterQuery = {
  actor: null,
  repo: null,
  box: null,
  level: null,
  text: [],
};

export function isEmptyTerminalFilter(query: TerminalFilterQuery): boolean {
  return (
    query.actor === null &&
    query.repo === null &&
    query.box === null &&
    query.level === null &&
    query.text.length === 0
  );
}

/** The `key=value` keys this grammar recognises — also what the command line Tab-completes. */
export const TERMINAL_FILTER_KEYS: readonly string[] = ['actor', 'repo', 'box', 'level'];

const KEYS: ReadonlySet<string> = new Set(TERMINAL_FILTER_KEYS);

/**
 * Split on whitespace, respecting a `"quoted value"` as one token (quotes
 * dropped) — including `key="quoted value"`, reassembled as `key=value`
 * before the plain-`\S+` alternative gets a chance to eat the quote-adjacent
 * `key="octocat/hello` as a single non-whitespace run and stop at the space
 * inside it.
 */
function tokenize(input: string): string[] {
  const tokens: string[] = [];
  const re = /([^\s"=]+)="([^"]*)"|"([^"]*)"|(\S+)/g;
  for (const m of input.matchAll(re)) {
    if (m[1] !== undefined && m[2] !== undefined) tokens.push(`${m[1]}=${m[2]}`);
    else if (m[3] !== undefined) {
      if (m[3] !== '') tokens.push(m[3]);
    } else if (m[4] !== undefined) tokens.push(m[4]);
  }
  return tokens;
}

/** Parse one `key=value` token, `null` when `key` is not one this grammar recognises. */
function parseKeyValue(
  token: string,
): { readonly key: keyof TerminalFilterQuery; readonly value: string } | null {
  const eq = token.indexOf('=');
  if (eq <= 0) return null;
  const key = token.slice(0, eq).toLowerCase();
  const value = token.slice(eq + 1);
  if (!KEYS.has(key) || value === '') return null;
  return { key: key as keyof TerminalFilterQuery, value };
}

/**
 * Parse a pane's filter box text into a query. Later tokens win for a
 * repeated key (`actor=coder actor=reviewer` -> `reviewer`) — the same "last
 * one wins" a browser URL's own `?a=1&a=2` resolves to, and simplest to
 * explain in a UI with no visible list of active clauses.
 */
export function parseTerminalFilter(input: string): TerminalFilterQuery {
  let actor: string | null = null;
  let repo: string | null = null;
  let box: string | null = null;
  let level: string | null = null;
  const text: string[] = [];

  for (const token of tokenize(input)) {
    const parsed = parseKeyValue(token);
    if (parsed === null) {
      text.push(token.toLowerCase());
      continue;
    }
    const value = parsed.value.toLowerCase();
    if (parsed.key === 'actor') actor = value;
    else if (parsed.key === 'repo') repo = value;
    else if (parsed.key === 'box') box = value;
    else level = value;
  }

  return { actor, repo, box, level, text };
}

/** The repo the row is about — `refs.repoFullName`, or the subject when it IS a repo. */
function repoOf(event: TerminalEvent): string | null {
  if (event.refs.repoFullName !== undefined) return event.refs.repoFullName;
  return event.subject.kind === 'repo' ? event.subject.label : null;
}

/** The box the row is about — `refs.boxId`, or the subject when it IS a box. */
function boxOf(event: TerminalEvent): string | null {
  if (event.refs.boxId !== undefined) return event.refs.boxId;
  return event.subject.kind === 'box' ? event.subject.label : null;
}

function includesCi(haystack: string | null, needle: string): boolean {
  return haystack?.toLowerCase().includes(needle) ?? false;
}

/**
 * A row's text as the pane RENDERS it — the sentence, its subject, its actor
 * and its level — lower-cased. The one definition both bare-text filtering
 * here and in-pane search (`search.ts`) match against, so "the filter finds
 * it" and "Ctrl+F finds it" can never disagree about what a line says.
 */
export function rowSearchText(event: TerminalEvent): string {
  return [
    terminalLineText(event.line),
    event.subject.label,
    actorLabel(event.actor),
    levelLabel(event.level),
  ]
    .join(' ')
    .toLowerCase();
}

/** Does `event` satisfy every clause of `query`? An empty query matches everything. */
export function matchesTerminalFilter(event: TerminalEvent, query: TerminalFilterQuery): boolean {
  if (query.actor !== null && (event.actor as TerminalActor).toLowerCase() !== query.actor) {
    return false;
  }
  if (query.level !== null && (event.level as TerminalLevel).toLowerCase() !== query.level) {
    return false;
  }
  if (query.repo !== null && !includesCi(repoOf(event), query.repo)) return false;
  if (query.box !== null && !includesCi(boxOf(event), query.box)) return false;

  if (query.text.length > 0) {
    const haystack = rowSearchText(event);
    if (!query.text.every((term) => haystack.includes(term))) return false;
  }

  return true;
}

/** `rows` narrowed to the ones `query` matches, oldest-first order preserved. */
export function filterTerminalRows(
  rows: readonly TerminalEvent[],
  query: TerminalFilterQuery,
): readonly TerminalEvent[] {
  return isEmptyTerminalFilter(query)
    ? rows
    : rows.filter((row) => matchesTerminalFilter(row, query));
}

// ─── verbose toggle (#4399 slice 08) ────────────────────────────────────────
//
// There is exactly ONE live connection for the whole window
// (`terminal-stream.ts`'s "one connection per tab") — every pane reads the
// SAME shared buffer, so the moment ANY pane's verbose toggle is on, the
// stream widens to `?verbose=true` and the shared buffer holds the
// `task_run_events` rows (tool_call, native_harness_turn, run_started/
// finished/released) alongside the audit ones. A pane whose OWN toggle is
// still off must not render them — this is that pane-local subtraction,
// composed as one more AND clause alongside `filterTerminalRows` above
// (`TerminalPane.tsx`'s `kindMatched`/`filteredRows` chain).
//
// DISCRIMINATOR: every verbose-family renderer keys its line under this
// prefix (`packages/domain/.../entities/terminal/renderers/verbose.ts`'s
// `projectTaskRunEvent` — every entry in `TERMINAL_RENDERERS_VERBOSE` emits
// `terminal.task_run_event.<kind>`, and no audit-family renderer uses that
// prefix). Client-side string match rather than importing the server-side
// table: the wire only publishes the rendered `line.key`, never which family
// produced it.
const VERBOSE_LINE_PREFIX = 'terminal.task_run_event.';

/** `true` for a row only the verbose (`task_run_events`) renderer family can produce. */
export function isVerboseOnlyLine(event: TerminalEvent): boolean {
  return event.line.key.startsWith(VERBOSE_LINE_PREFIX);
}

/** `rows` narrowed to the audit-only set when `verbose` is false; unchanged when `true`. */
export function filterVerboseRows(
  rows: readonly TerminalEvent[],
  verbose: boolean,
): readonly TerminalEvent[] {
  return verbose ? rows : rows.filter((row) => !isVerboseOnlyLine(row));
}
